import React, { useState, useEffect, useRef } from "react";
import MessageBubble from "./MessageBubble";
import sendMessageToAI from "../services/groqService";
import "../styles/aiAssistant.css";
import logo from "../assets/agencybazaar-logo.png";

const AIChatWindow = ({ onClose }) => {
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [isVoiceQuery, setIsVoiceQuery] = useState(false);

  const recognitionRef = useRef(null);
  const messagesEndRef = useRef(null);

  const quickQuestions = [
    "What is AgencyBazaar?",
    "How to Join?",
    "Income Potential",
    "Partner Companies",
  ];

  // ✅ Speech Recognition Setup
  useEffect(() => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.lang = "hi-IN";
      recognition.continuous = false;
      recognition.interimResults = false;

      recognition.onresult = (e) => {
        const transcript = e.results[0][0].transcript;
        setInput(transcript);
        setIsListening(false);
        setIsVoiceQuery(true); // voice se aaya hai
      };

      recognition.onerror = () => setIsListening(false);
      recognition.onend = () => setIsListening(false);

      recognitionRef.current = recognition;
    }
  }, []);

  // ✅ Auto Scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // ✅ NISHI Intro (logo click pe)
  const handleNishiIntro = () => {
    const speech = new SpeechSynthesisUtterance(
      "Hello, I am NISHI, the AI Assistant of Agency Bazaar, founded by Mr. Sanjeev Kumar. How can I assist you today?",
    );
    speech.lang = "hi-IN";
    speech.rate = 1;
    speech.pitch = 1;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(speech);
  };

  // ✅ Voice Output
  const speakText = (text) => {
    if (!voiceEnabled) return;
    window.speechSynthesis.cancel();
    const utter = new SpeechSynthesisUtterance(text);
    utter.lang = "hi-IN";
    utter.rate = 1;
    utter.pitch = 1;
    window.speechSynthesis.speak(utter);
  };

  // ✅ Mic Toggle
  const handleMic = () => {
    if (!recognitionRef.current) return;
    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      recognitionRef.current.start();
      setIsListening(true);
    }
  };

  // ✅ Send Message
  const handleSend = async (customMessage = null) => {
    const finalMessage =
      typeof customMessage === "string" ? customMessage : input;
    if (!finalMessage.trim()) return;

    const userMessage = { sender: "user", text: finalMessage };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    const aiReply = await sendMessageToAI(finalMessage);
    setLoading(false);

    // ✅ Typing + Voice saath saath shuru
    const tempId = Date.now();
    setMessages((prev) => [...prev, { sender: "ai", text: "", id: tempId }]);

    if (isVoiceQuery && voiceEnabled) speakText(aiReply); // sirf voice query pe bolega
    setIsVoiceQuery(false); // reset karo

    let currentText = "";
    for (let i = 0; i < aiReply.length; i++) {
      currentText += aiReply[i];
      const snapshot = currentText;
      await new Promise((resolve) => setTimeout(resolve, 18));
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === tempId ? { ...msg, text: snapshot } : msg,
        ),
      );
    }
  };

  return (
    <div className="ai-chat-window">
      {/* Header */}
      <div className="ai-chat-header">
        <div>
          <h3>AgencyBazaar AI</h3>
          <span>Online</span>
        </div>

        {/* Voice toggle */}
        <button
          onClick={() => {
            setVoiceEnabled(!voiceEnabled);
            window.speechSynthesis.cancel();
          }}
          title={voiceEnabled ? "Voice Off" : "Voice On"}
          style={{
            fontSize: "18px",
            marginRight: "8px",
            background: "transparent",
            border: "none",
            color: "white",
            cursor: "pointer",
          }}
        >
          {voiceEnabled ? "🔊" : "🔇"}
        </button>

        <button onClick={onClose}>✕</button>
      </div>

      {/* Body */}
      <div className="ai-chat-body">
        {messages.length === 0 && (
          <div className="ai-welcome-section">
            <div className="ai-avatar-container">
              <img
                src={logo}
                alt="NISHI"
                className="ai-avatar"
                onClick={handleNishiIntro}
              />
            </div>
            <h2 className="nishi-title">NISHI</h2>
            <p className="nishi-subtitle">Insurance Career Assistant</p>
          </div>
        )}

        {messages.map((msg, index) => (
          <MessageBubble key={index} sender={msg.sender} text={msg.text} />
        ))}

        {messages.length === 0 && (
          <div className="quick-questions">
            {quickQuestions.map((question, index) => (
              <button
                key={index}
                className="quick-question-btn"
                onClick={() => handleSend(question)}
              >
                {question}
              </button>
            ))}
          </div>
        )}

        {loading && <MessageBubble sender="ai" text="Typing..." />}
        <div ref={messagesEndRef}></div>
      </div>

      {/* Footer */}
      <div className="ai-chat-footer">
        <input
          type="text"
          placeholder={isListening ? "🎤 Sun rahi hoon..." : "Ask anything..."}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSend();
          }}
        />

        {/* Mic Button */}
        <button
          onClick={handleMic}
          className={`mic-btn ${isListening ? "listening" : ""}`}
          title="Voice Input"
        >
          {isListening ? "⏹" : "🎤"}
        </button>

        {/* Send Button */}
        <button onClick={handleSend}>➤</button>
      </div>
    </div>
  );
};

export default AIChatWindow;
